/** Loading, empty, error and offline states — every screen ships all four. */

import type { ReactNode } from 'react';
import { useI18n } from '../lib/i18n';
import { Button } from './primitives';
import { Icon, type IconName } from './Icon';

/* ── Skeletons ───────────────────────────────────────────────────────────
   Shaped exactly like the content they stand in for, so the swap to real data
   does not move anything. */

export function SkeletonLine({ w = '100%', h = 12 }: { w?: string; h?: number }) {
  return (
    <span
      aria-hidden="true"
      className="block rounded bg-media-placeholder"
      style={{ width: w, height: h }}
    />
  );
}

export function SkeletonCard({ ratio = '4 / 5' }: { ratio?: string }) {
  return (
    <div className="flex flex-col gap-2 rounded-card bg-surface p-2">
      <span
        aria-hidden="true"
        className="pv-shimmer block rounded-thumb"
        style={{ aspectRatio: ratio }}
      />
      <span className="flex flex-col gap-1.5 px-0.5 pb-0.5">
        <SkeletonLine w="88%" />
        <SkeletonLine w="56%" />
        <SkeletonLine w="40%" h={10} />
      </span>
    </div>
  );
}

export function SkeletonGrid({ count = 6 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-2" aria-busy="true">
      {Array.from({ length: count }, (_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}

export function SkeletonRail({ count = 4, width = 160 }: { count?: number; width?: number }) {
  return (
    <div className="sc -mx-gutter flex gap-2 overflow-x-auto px-gutter" aria-busy="true">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} style={{ width }} className="flex-none">
          <SkeletonCard />
        </div>
      ))}
    </div>
  );
}

export function SkeletonDetail() {
  return (
    <div aria-busy="true">
      <span aria-hidden="true" className="pv-shimmer block h-[320px] w-full" />
      <div className="flex flex-col gap-3 bg-surface px-gutter py-4">
        <SkeletonLine w="82%" h={20} />
        <SkeletonLine w="46%" h={20} />
        <SkeletonLine w="60%" />
      </div>
      <div className="px-gutter pt-3">
        <div className="flex flex-col gap-2 rounded-card bg-surface p-4">
          <SkeletonLine w="100%" />
          <SkeletonLine w="94%" />
          <SkeletonLine w="72%" />
        </div>
      </div>
    </div>
  );
}

export function SkeletonComments({ count = 3 }: { count?: number }) {
  return (
    <div className="flex flex-col gap-2" aria-busy="true">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="flex gap-2.5 rounded-card bg-surface p-3">
          <span
            aria-hidden="true"
            className="h-[34px] w-[34px] flex-none rounded-full bg-media-placeholder"
          />
          <span className="flex flex-1 flex-col gap-1.5">
            <SkeletonLine w="38%" h={10} />
            <SkeletonLine w="92%" />
            <SkeletonLine w="64%" />
          </span>
        </div>
      ))}
    </div>
  );
}

export function SkeletonCells({ count = 5 }: { count?: number }) {
  return (
    <div className="overflow-hidden rounded-card bg-surface" aria-busy="true">
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          className={`flex items-center gap-3 px-4 py-3 ${i === count - 1 ? '' : 'hairline-b'}`}
        >
          <span
            aria-hidden="true"
            className="h-[30px] w-[30px] flex-none rounded-thumb bg-media-placeholder"
          />
          <span className="flex flex-1 flex-col gap-1.5">
            <SkeletonLine w="52%" />
            <SkeletonLine w="30%" h={10} />
          </span>
        </div>
      ))}
    </div>
  );
}

/* ── Placeholder (empty / error) ─────────────────────────────────────────── */

export function Placeholder({
  icon,
  title,
  description,
  action,
  compact = false,
}: {
  icon: IconName;
  title: string;
  description?: ReactNode;
  action?: { label: string; onClick: () => void };
  compact?: boolean;
}) {
  return (
    <div
      className={`pv-rise flex flex-col items-center gap-2.5 text-center ${
        compact ? 'px-8 py-10' : 'px-10 py-16'
      }`}
    >
      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-fill-strong text-hint">
        <Icon name={icon} size={30} strokeWidth={1.8} />
      </span>
      <p className="text-headline font-semibold text-text">{title}</p>
      {description ? (
        <p className="text-balance text-subhead leading-relaxed text-hint">{description}</p>
      ) : null}
      {action ? (
        <Button onClick={action.onClick} className="mt-2">
          {action.label}
        </Button>
      ) : null}
    </div>
  );
}

export function ErrorState({
  onRetry,
  message,
  compact = false,
}: {
  onRetry: () => void;
  message?: string;
  compact?: boolean;
}) {
  const { t } = useI18n();
  return (
    <Placeholder
      icon="close"
      title={t.errorTitle}
      description={message ?? t.errorSub}
      action={{ label: t.retry, onClick: onRetry }}
      compact={compact}
    />
  );
}

/** Sticky banner while the device is offline; cached query data stays on screen beneath it. */
export function OfflineBanner() {
  const { t } = useI18n();
  return (
    <div className="sticky top-0 z-20 bg-fill-strong px-gutter py-2 text-center text-footnote text-hint">
      {t.offline}
    </div>
  );
}
